/**
 * Parent Message Initializer Component
 *
 * Sets up the parent message listener at app initialization.
 * This enables bidirectional communication between the Ramps UI
 * and the parent application (web iframe, iOS WKWebView, or Android ReactNativeWebView).
 *
 * Also sends a READY message to the parent when the UI has loaded,
 * signaling that it can receive messages.
 */

import { useCallback, useEffect, useRef } from "react";
import { useAnalytics } from "@/hooks/usePostHog";
import { ANALYTICS_EVENTS } from "@/utils/analytics";
import {
	isEmbedded,
	setOnInitTransactionCallback,
	useParentMessageListener,
} from "../lib/parent-message-handler";
import { useWebViewIntegration } from "../lib/url-state";

export function ParentMessageInitializer() {
	const hasSentReady = useRef(false);
	const { sendToWebView } = useWebViewIntegration();
	const { trackEvent } = useAnalytics();

	// Set up the message listener
	useParentMessageListener();

	// Track INIT_TRANSACTION events from parent
	const handleInitTransaction = useCallback(
		(payload: Record<string, unknown>) => {
			trackEvent(ANALYTICS_EVENTS.EMBEDDED_INIT_RECEIVED, {
				wallet_provider: String(payload.wallet_provider || ""),
				asset_code: String(payload.asset_code || ""),
				operation: String(payload.operation || ""),
				message_type: "INIT_TRANSACTION",
			});
		},
		[trackEvent],
	);

	useEffect(() => {
		setOnInitTransactionCallback(handleInitTransaction);
		return () => setOnInitTransactionCallback(null);
	}, [handleInitTransaction]);

	// Send READY message to parent when the UI loads
	useEffect(() => {
		if (hasSentReady.current) return;

		if (isEmbedded()) {
			// Small delay to ensure parent listener is set up
			const timer = setTimeout(() => {
				sendToWebView("READY", {
					version: "1.0",
					supportedMessages: [
						"INIT_TRANSACTION",
						"COMMIT_PROCEED",
						"COMMIT_ERROR",
					],
				});
				hasSentReady.current = true;
			}, 100);

			return () => clearTimeout(timer);
		}
	}, [sendToWebView]);

	// This is an initialization component - renders nothing
	return null;
}
